import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from './ui/table';
import { Badge } from './ui/badge';
import { Button } from './ui/button';
import { Loader2, Calendar, CalendarX, Filter } from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Leads = () => {
  const [quotes, setQuotes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all'); // 'all', 'scheduled', 'unscheduled'

  useEffect(() => {
    fetchQuotes();
  }, []);

  const fetchQuotes = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/quotes`);
      setQuotes(response.data);
    } catch (error) {
      console.error('Error fetching quotes:', error);
      toast.error('Failed to load leads');
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const filteredQuotes = quotes.filter(quote => {
    if (filter === 'scheduled') return quote.appointmentDate && quote.appointmentTime;
    if (filter === 'unscheduled') return !quote.appointmentDate || !quote.appointmentTime;
    return true;
  });

  const unscheduledCount = quotes.filter(q => !q.appointmentDate || !q.appointmentTime).length;
  const scheduledCount = quotes.filter(q => q.appointmentDate && q.appointmentTime).length;

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="h-12 w-12 animate-spin text-emerald-600 mx-auto mb-4" />
          <p className="text-slate-600">Loading leads...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50 py-12 px-4">
      <div className="container mx-auto max-w-7xl">
        <Card>
          <CardHeader>
            <CardTitle className="text-3xl">Leads Dashboard</CardTitle>
            <CardDescription>
              View all customer quote requests and track scheduling status
            </CardDescription>
            
            {/* Filter Buttons */}
            <div className="flex gap-3 mt-4">
              <Button
                variant={filter === 'all' ? 'default' : 'outline'}
                onClick={() => setFilter('all')}
                className={filter === 'all' ? 'bg-emerald-600 hover:bg-emerald-700' : ''}
              >
                <Filter className="h-4 w-4 mr-2" />
                All Leads ({quotes.length})
              </Button>
              <Button
                variant={filter === 'unscheduled' ? 'default' : 'outline'}
                onClick={() => setFilter('unscheduled')}
                className={filter === 'unscheduled' ? 'bg-amber-600 hover:bg-amber-700' : ''}
              >
                <CalendarX className="h-4 w-4 mr-2" />
                Not Scheduled ({unscheduledCount})
              </Button>
              <Button
                variant={filter === 'scheduled' ? 'default' : 'outline'}
                onClick={() => setFilter('scheduled')}
                className={filter === 'scheduled' ? 'bg-blue-600 hover:bg-blue-700' : ''}
              >
                <Calendar className="h-4 w-4 mr-2" />
                Scheduled ({scheduledCount})
              </Button>
            </div>
          </CardHeader>
          <CardContent>
            {filteredQuotes.length === 0 ? (
              <div className="text-center py-12">
                <p className="text-slate-500 text-lg">No {filter !== 'all' ? filter : ''} leads found</p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Submitted</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead>Customer</TableHead>
                      <TableHead>Contact</TableHead>
                      <TableHead>Garage Size</TableHead>
                      <TableHead>Services</TableHead>
                      <TableHead className="text-right">Total Price</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {filteredQuotes.map((quote) => (
                      <TableRow key={quote.id}>
                        <TableCell className="text-sm text-slate-600">
                          {formatDate(quote.createdAt)}
                        </TableCell>
                        <TableCell>
                          {quote.appointmentDate && quote.appointmentTime ? (
                            <Badge className="bg-blue-100 text-blue-700 hover:bg-blue-100">
                              Scheduled
                            </Badge>
                          ) : (
                            <Badge variant="outline" className="text-amber-600 border-amber-300">
                              Not Scheduled
                            </Badge>
                          )}
                        </TableCell>
                        <TableCell>
                          <div className="space-y-1">
                            {quote.name && <div className="font-semibold text-slate-900">{quote.name}</div>}
                            {quote.zipCode && (
                              <div className="text-xs text-slate-500">
                                ZIP: {quote.zipCode}
                                {quote.distance && ` (${quote.distance.toFixed(1)} mi)`}
                              </div>
                            )}
                          </div>
                        </TableCell>
                        <TableCell>
                          <div className="space-y-1 text-sm">
                            <div className="text-slate-700">{quote.email}</div>
                            <div className="text-slate-600">{quote.phone}</div>
                          </div>
                        </TableCell>
                        <TableCell>
                          <Badge variant="outline" className="capitalize">
                            {quote.garageSize}
                          </Badge>
                        </TableCell>
                        <TableCell>
                          <div className="flex flex-wrap gap-1">
                            {quote.heavyItems && (
                              <Badge className="bg-emerald-100 text-emerald-700 hover:bg-emerald-100 text-xs">
                                Heavy Items
                              </Badge>
                            )}
                            {quote.sweeping && (
                              <Badge className="bg-emerald-100 text-emerald-700 hover:bg-emerald-100 text-xs">
                                Sweeping
                              </Badge>
                            )}
                            {quote.pressureWashing && (
                              <Badge className="bg-emerald-100 text-emerald-700 hover:bg-emerald-100 text-xs">
                                Pressure Wash
                              </Badge>
                            )}
                            {!quote.heavyItems && !quote.sweeping && !quote.pressureWashing && (
                              <span className="text-sm text-slate-500">Base only</span>
                            )}
                          </div>
                        </TableCell>
                        <TableCell className="text-right font-bold text-lg">
                          ${quote.totalPrice.toFixed(2)}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            )}
            
            <div className="mt-6 pt-6 border-t border-slate-200">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="bg-slate-100 p-4 rounded-lg">
                  <p className="text-sm text-slate-600 mb-1">Total Leads</p>
                  <p className="text-2xl font-bold text-slate-900">{quotes.length}</p>
                </div>
                <div className="bg-amber-50 p-4 rounded-lg">
                  <p className="text-sm text-amber-700 mb-1">Not Scheduled</p>
                  <p className="text-2xl font-bold text-amber-900">{unscheduledCount}</p>
                </div>
                <div className="bg-blue-50 p-4 rounded-lg">
                  <p className="text-sm text-blue-700 mb-1">Scheduled</p>
                  <p className="text-2xl font-bold text-blue-900">{scheduledCount}</p>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default Leads;
